/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/*
Author: Granite Bagas
*/

import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik } from 'formik';
import Toast from 'react-native-toast-message';
import { View, StyleSheet, ScrollView, Dimensions } from 'react-native';
import SociollaText from '@components/sociolla/SociollaText';
import Image from '@components/global/Image';
import Colors from '@global/colors';
import Button from '@components/form/Button';
import * as Layout from '@components/screen/Layout';
import Input from '@components/form/Input';
import WarehouseSearch from '@components/form/search/WarehouseSearch';
import { isWeb, isMobileWeb, isAndroid } from '@global/screens';
import { showPopup } from '@/store/modal';
import { showAlert } from '@/store/global';
import useApi from '@/hooks/useApi';
import { MsOrder } from '@api/ms-order';
import { MsB2b } from '@api/ms-b2b';
import ProductItem from '@components/global/ProductItem';
import {
  SociollaSelectBox,
  SociollaSalesTeamSearch,
} from '@components/sociolla/form';
import AddProduct from '@/screens/inventory/components/AddProduct';
import ESignature from '@/screens/inventory/components/ESignature';
import ProgressBar from '@components/global/ProgressBar';
import MoreButton from '@components/navbar/MoreButton';
import SalesPersonSearch from '@components/form/search/SalesPersonSearch';

const dimension = Dimensions.get('screen');

const InventoryTypeOption = [
  {
    value: 'add',
    label: 'Add Inventory',
  },
  {
    value: 'return',
    label: 'Return Inventory',
  },
];

const InventoryFormScreen = (props) => {
  const dispatch = useDispatch();
  const formRef = useRef();
  const auth = useSelector((state) => state.auth);
  const role = useSelector((state) => state.auth.user?.role?.code || '');

  const [detailData, setDetailData] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [loadingAddProduct, setLoadingAddProduct] = useState(false);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [customFilter, setCustomFilter] = useState({});
  const [products, setProducts] = useState([]);
  const [productDetail, setProductDetail] = useState({});
  const [warehouseData, setWarehouseData] = useState(null);
  const [page, setPage] = useState(1);
  const totalPage = 3;

  const getInventoryDetail = useApi(MsOrder.getInventoryDetail);

  const salesTeamFilter = useMemo(() => {
    const filter = Boolean(
      ['salesman', 'branch_head', 'sales_admin', 'spv_logistic'].indexOf(role) >
        -1,
    );
    if (filter) {
      return {
        _id: { $in: auth.user.sales_team.map((x) => x.id) },
      };
    } else {
      return {};
    }
  }, [role]);

  const getDetail = async (id) => {
    const response = await getInventoryDetail.request(id);
    if (response?.data?.data && Object.keys(response.data.data).length) {
      const result = response.data.data;
      result.id = result._id;

      setDetailData(result);
      setWarehouseData(result.warehouse);
      await getProducts(result);
      getSalesTeam(result.sales_team.id);
      setIsReady(true);
    }
  };

  const calculateAvailableStock = async (
    warehouse_id,
    inventory_type,
    salesman_id,
    products,
  ) => {
    const response = await MsOrder.calculateInventoryStock({
      warehouse_id,
      inventory_type,
      salesman_id,
      products,
    });
    setProducts(response?.data?.data);
  };

  const getProducts = async (detail) => {
    await calculateAvailableStock(
      detail.warehouse.pacman_warehouse_id,
      detail.inventory_type,
      detail.user.id,
      detail.products,
    );
  };

  const getSalesTeam = (id) => {
    MsB2b.getSalesTeam({
      params: {
        filter: {
          _id: id,
        },
      },
    }).then((res) => {
      if (res.status === 200 && res.data?.data?.length) {
        const salesman = [];
        const beautyAdvisors = [];
        const merchandisers = [];
        res.data.data[0].team_members.map((user) => {
          if (user?.role?.code == 'salesman') {
            salesman.push(user);
          } else if (user?.role?.code == 'beauty_advisor') {
            beautyAdvisors.push(user);
          } else if (user?.role?.code == 'merchandiser') {
            merchandisers.push(user);
          }
        });
        getSalesPerson(salesman);
      }
    });
  };

  const onChangeSalesTeam = (val) => {
    const salesman = [];
    val.team_members.map((user) => {
      if (user?.role?.code == 'salesman') {
        salesman.push(user);
      }
    });
    getSalesPerson(salesman);
  };

  const getSalesPerson = (sales = []) => {
    let ids = sales.map((a) => a.id);
    if (role == 'salesman') {
      ids = ids.filter((id) => id.toString() == auth.userId);
    }
    setCustomFilter({
      remove_default_filter: true,
      is_active: true,
      _id: { $in: ids },
    });
  };

  const resetData = () => {
    setDetailData({});
    setIsEdit(false);
    setIsReady(false);
    setCustomFilter({});
    setProducts([]);
    setProductDetail({});
    setWarehouseData(null);
  };

  const submitForm = async (payload, params) => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      let finalPayload = {
        status: payload.status,
        inventory_type: payload.inventory_type,
        user: {
          id: payload.salesman.id,
          name: payload.salesman.name,
          sales_code: payload.salesman.sales_code,
          image: payload.salesman.image,
        },
        sales_team: {
          id: payload.sales_team.id,
          name: payload.sales_team.name,
        },
        warehouse: {
          id: payload.warehouse.id,
          name: payload.warehouse.name,
          pacman_warehouse_id: payload.warehouse.pacman_warehouse_id,
        },
        products,
        esign_requestor: {
          name: payload.esign_requestor_name,
          image: payload.esign_requestor,
        },
        esign_approver: {
          name: payload.esign_approver_name,
          image: payload.esign_approver,
        },
      };

      // Filter out null, undefined, and empty string values
      finalPayload = Object.fromEntries(
        Object.entries(finalPayload).filter(
          ([key, value]) =>
            value !== null && value !== undefined && value !== '',
        ),
      );

      // Check products stock
      const emptyStock = finalPayload.products.find((p) => p.final_stock < 0);
      if (emptyStock && payload.status == 'submitted') {
        dispatch(showPopup({ name: 'isLoading', value: false }));
        Toast.show({
          text1: `Stock in ${
            finalPayload.inventory_type == 'add' ? 'warehouse' : 'inventory'
          } is not enough`,
          type: 'error',
        });
        return;
      }

      if (!isEdit) {
        const createData = await MsOrder.createInventory(finalPayload);
        if (createData?.data?.data) {
          const result = createData.data.data;
          const title =
            finalPayload.status == 'submitted'
              ? 'New Inventory Submitted'
              : 'New Inventory Added';
          const desc =
            finalPayload.status == 'submitted'
              ? 'New inventory successfully submitted.'
              : 'New inventory successfully added.';
          dispatch(showPopup({ name: 'isLoading', value: false }));
          dispatch(
            showAlert({
              show: true,
              title,
              description: desc,
              hideCancel: true,
              onCallback: () => {
                resetData();
                params.resetForm();
                if (result._id) {
                  props.navigation.navigate('InventoryDetailScreen', {
                    titleScreen: 'Detail Inventory',
                    name: result.user.name,
                    id: result._id,
                  });
                } else {
                  props.navigation.navigate('InventoryScreen');
                }
              },
            }),
          );
        } else {
          dispatch(showPopup({ name: 'isLoading', value: false }));
          Toast.show({
            text1: 'A system error occurred',
            type: 'error',
          });
        }
      } else {
        const updateData = await MsOrder.updateInventory(
          detailData._id,
          finalPayload,
        );
        if (updateData?.data?.data) {
          const result = updateData.data.data;
          dispatch(showPopup({ name: 'isLoading', value: false }));
          Toast.show({
            text1: 'Successfully edited data',
            type: 'success',
          });
          resetData();
          params.resetForm();
          props.navigation.navigate('InventoryDetailScreen', {
            titleScreen: 'Detail Inventory',
            name: result.user.name,
            id: result._id,
          });
        } else {
          dispatch(showPopup({ name: 'isLoading', value: false }));
          Toast.show({
            text1: 'A system error occurred',
            type: 'error',
          });
        }
      }
      dispatch(showPopup({ name: 'isLoading', value: false }));
    } catch (error) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
      Toast.show({
        text1: error?.response?.data?.message || 'A system error occurred',
        type: 'error',
      });
    }
  };

  const goBack = () => {
    props.navigation.replace('InventoryFormScreen', {});
    if (isEdit) {
      props.navigation.navigate('InventoryDetailScreen', props.route.params);
    } else {
      props.navigation.navigate('InventoryScreen');
    }
  };

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      setIsEdit(false);
      setDetailData({});
      setIsReady(false);
      if (props?.route?.params?.id) {
        setIsEdit(true);
        getDetail(props.route.params.id);
      } else {
        setTimeout(() => {
          setIsReady(true);
        }, 500);
      }
    }
    return () => {
      mounted = false;
    };
  }, [props]);

  useEffect(() => {
    if (!isWeb && [2, 3].includes(page)) {
      const handleSaveDraft = async () => {
        formRef.current?.setFieldValue('status', 'draft');
        await submitForm(formRef.current?.values, formRef.current);
      };

      const menus = [
        {
          type: 'function',
          name: 'Save Draft',
          callback: handleSaveDraft,
        },
      ];

      props.navigation.setOptions({
        headerRight: () => <MoreButton menu={menus} {...props} />,
      });
    } else {
      props.navigation.setOptions({
        headerRight: () => false,
      });
    }
  }, [page, props, products]);

  // show add product popup
  const onAddProduct = (val = {}) => {
    setProductDetail(val);
    setShowAddProduct(true);
  };

  // show add product popup
  const onCloseProduct = () => {
    setProductDetail({});
    setShowAddProduct(false);
  };

  // remove product from sales order form with popup confirmation
  const onRemoveProduct = (index) => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    dispatch(
      showAlert({
        show: true,
        title: 'Remove Product',
        description: 'This product will be deleted. Do you want to proceed?',
        onCallback: async () => {
          const pr = JSON.parse(JSON.stringify(products));
          const removedProduct = pr.splice(index, 1);
          if (isEdit) {
            await MsOrder.deleteInventoryProduct(
              detailData.id,
              removedProduct[0].id,
              removedProduct[0].combination.id,
            );
          }
          await calculateAvailableStock(
            warehouseData.pacman_warehouse_id,
            formRef.current?.values?.inventory_type,
            formRef.current?.values?.salesman.id,
            pr,
          );
        },
      }),
    );
    dispatch(showPopup({ name: 'isLoading', value: false }));
  };

  // condition for request confirmation button
  const isAvailableSubmit = (val) => {
    const requiredFields = Boolean(
      val.inventory_type &&
        val?.sales_team?.id &&
        val?.salesman?.id &&
        val?.warehouse?.id &&
        products?.length > 0 &&
        val?.esign_approver_name &&
        val?.esign_requestor &&
        val?.esign_approver_name &&
        val?.esign_approver,
    );
    return requiredFields;
  };

  const isAvailableDraft = (val) => {
    const requiredFields = Boolean(
      val.inventory_type &&
        val?.sales_team?.id &&
        val?.salesman?.id &&
        val?.warehouse?.id,
    );
    return requiredFields;
  };

  return (
    <Layout.Container>
      {!isWeb && <ProgressBar page={page} totalPage={totalPage} />}
      {(isEdit && getInventoryDetail?.loading) || !isReady ? (
        <Layout.Wrapper>
          <Layout.Loader />
        </Layout.Wrapper>
      ) : (isEdit ? detailData && Object.keys(detailData).length : true) ? (
        <Formik
          innerRef={formRef}
          initialValues={{
            inventory_type:
              isEdit && detailData?.inventory_type
                ? detailData.inventory_type
                : '',
            sales_team:
              isEdit && detailData?.sales_team ? detailData.sales_team : '',
            salesman: isEdit && detailData?.user ? detailData.user : '',
            warehouse:
              isEdit && detailData?.warehouse ? detailData.warehouse : '',
            esign_requestor_name:
              isEdit && detailData?.esign_requestor.name
                ? detailData.esign_requestor.name
                : '',
            esign_approver_name:
              isEdit && detailData?.esign_approver.name
                ? detailData.esign_approver.name
                : '',
            esign_requestor:
              isEdit && detailData?.esign_requestor.image
                ? detailData.esign_requestor.image
                : '',
            esign_approver:
              isEdit && detailData?.esign_approver.image
                ? detailData.esign_approver.image
                : '',
          }}
          // validationSchema={FormValidationSchema}
          onSubmit={submitForm}>
          {({ setFieldValue, handleSubmit, errors, touched, values }) => (
            <>
              {isWeb ? (
                <Layout.Wrapper white={false}>
                  <Layout.Back title="Inventory" onCallback={() => goBack()} />
                  <Layout.Title
                    title={`${isEdit ? `Edit` : `Create New`} Inventory`}>
                    <View style={styles.row}>
                      <View style={{ marginRight: 10 }}>
                        <Button
                          type="primary"
                          rounded
                          buttonStyle={{ width: 120 }}
                          disabled={!isAvailableDraft(values)}
                          title={
                            <SociollaText
                              type="bold"
                              style={{
                                fontSize: 14,
                                letterSpacing: 0.5,
                                color: Colors.white,
                              }}>
                              {isEdit ? 'SAVE' : 'Save Draft'}
                            </SociollaText>
                          }
                          onPress={(evt) => {
                            setFieldValue('status', 'draft');
                            handleSubmit(evt);
                          }}
                        />
                      </View>
                      <Button
                        type="primary"
                        rounded
                        buttonStyle={{
                          width: 185,
                          marginRight: 10,
                          backgroundColor: Colors.blue0,
                        }}
                        disabled={!isAvailableSubmit(values)}
                        title={
                          <SociollaText
                            type="bold"
                            style={{
                              fontSize: 14,
                              letterSpacing: 0.5,
                              color: Colors.white,
                            }}>
                            Submit
                          </SociollaText>
                        }
                        onPress={(evt) => {
                          setFieldValue('status', 'submitted');
                          handleSubmit(evt);
                        }}
                      />
                    </View>
                  </Layout.Title>
                </Layout.Wrapper>
              ) : (
                false
              )}
              <Layout.Wrapper rounded hasProgressBar>
                <ScrollView
                  style={isMobileWeb ? { height: dimension.height - 85 } : {}}
                  contentContainerStyle={{ width: '100%' }}>
                  {page == 1 || isWeb ? (
                    <Layout.FormWrapper
                      style={{
                        paddingHorizontal: 15,
                        paddingTop: 20,
                        ...(isWeb
                          ? {
                              flexDirection: 'row',
                              justifyContent: 'space-between',
                              paddingHorizontal: 20,
                              marginBottom: 15,
                            }
                          : {
                              paddingBottom: !isMobileWeb
                                ? isAndroid
                                  ? 200
                                  : 150
                                : 180,
                            }),
                      }}>
                      <View style={styles.rowForm}>
                        <Layout.FormGroup>
                          <SociollaSelectBox
                            label="Inventory Type"
                            options={InventoryTypeOption}
                            name="inventory_type"
                            trackBy="value"
                            labelKey="label"
                            isRequired
                            onChange={async (val) => {
                              setFieldValue('inventory_type', val.value);
                              await calculateAvailableStock(
                                values.warehouse?.pacman_warehouse_id ||
                                  values.warehouse,
                                val.value,
                                values.salesman.id,
                                products,
                              );
                            }}
                          />
                        </Layout.FormGroup>
                        <Layout.FormGroup>
                          <SociollaSalesTeamSearch
                            customFilter={salesTeamFilter}
                            name="sales_team"
                            onChange={onChangeSalesTeam}
                            isRequired
                          />
                        </Layout.FormGroup>
                        <Layout.FormGroup>
                          <SalesPersonSearch
                            isRequired
                            label="Salesman"
                            labelKey="sales_code"
                            value={values.salesman}
                            editable={values['sales_team'] ? true : false}
                            disabled={values['sales_team'] ? false : true}
                            customFilter={customFilter}
                            onChange={async (val) => {
                              setFieldValue('salesman', val);
                              await calculateAvailableStock(
                                values.warehouse?.pacman_warehouse_id ||
                                  values.warehouse,
                                values.inventory_type,
                                val.id,
                                products,
                              );
                            }}
                            useCallbackOptions={[customFilter]}
                          />
                        </Layout.FormGroup>
                      </View>
                      <View style={styles.rowForm}>
                        <Layout.FormGroup>
                          <WarehouseSearch
                            label="Warehouse"
                            value={values.warehouse}
                            idSales={values.sales_team}
                            hasError={
                              errors.warehouse && touched.warehouse
                                ? errors.warehouse
                                : null
                            }
                            editable={values.sales_team?.id ? true : false}
                            isRequired={true}
                            onChange={async (value) => {
                              if (!value.pacman_warehouse_id) {
                                Toast.show({
                                  text1:
                                    'No Sales team Warehouse found to get the product stock from pacman!',
                                  type: 'error',
                                });
                                setFieldValue('warehouse', '');
                                setWarehouseData(null);
                                return;
                              }
                              setFieldValue('warehouse', value);
                              setWarehouseData(value);
                              await calculateAvailableStock(
                                value.pacman_warehouse_id || value,
                                values.inventory_type?.value ||
                                  values.inventory_type,
                                values.salesman.id,
                                products,
                              );
                            }}
                            useCallbackOptions={[values.sales_team]}
                          />
                        </Layout.FormGroup>
                      </View>
                    </Layout.FormWrapper>
                  ) : (
                    false
                  )}
                  {isWeb ? (
                    <View
                      style={{
                        width: '100%',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: Colors.grey4,
                        paddingHorizontal: 20,
                        paddingVertical: 12,
                      }}>
                      <SociollaText
                        type="bold"
                        style={{
                          fontSize: isWeb ? 16 : 13,
                          letterSpacing: 0.5,
                          color: Colors.grey1,
                        }}>
                        PRODUCT
                      </SociollaText>
                      <View
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                        }}>
                        <Button
                          type="default"
                          rounded
                          buttonStyle={{ width: isWeb ? 150 : 85 }}
                          disabledStyle={{ width: isWeb ? 150 : 85 }}
                          disabled={!warehouseData}
                          title={
                            <SociollaText
                              type="bold"
                              style={{
                                fontSize: isWeb ? 14 : 13,
                                letterSpacing: 0.5,
                                color: Colors.black,
                              }}>
                              Add Product
                            </SociollaText>
                          }
                          onPress={() => {
                            onAddProduct();
                          }}
                        />
                      </View>
                    </View>
                  ) : (
                    false
                  )}
                  {page == 2 || isWeb ? (
                    <ScrollView
                      style={{
                        width: '100%',
                        paddingHorizontal: isWeb ? 30 : 0,
                        ...(isWeb
                          ? { paddingVertical: 20 }
                          : {
                              paddingBottom: 200,
                            }),
                      }}>
                      {products?.length ? (
                        (products || []).map((item, idx) => (
                          <ProductItem
                            data={item}
                            orderId={detailData?._id}
                            onShow={() => onAddProduct(item)}
                            onRemove={() => onRemoveProduct(idx)}
                            isShowStock={true}
                            showRemove={true}
                            key={idx}
                            withoutDiscount={true}
                            withoutCurrency={true}
                          />
                        ))
                      ) : !loadingAddProduct ? (
                        <View
                          style={{
                            flex: 1,
                            paddingVertical: 20,
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}>
                          <Image
                            style={{ width: 30, height: 35 }}
                            width={30}
                            height={35}
                            source={require('@assets/img/icons/no-product.svg')}
                            svg={true}
                          />
                          <SociollaText style={{ marginVertical: 10 }}>
                            No product found
                          </SociollaText>
                          {!isWeb ? (
                            <View
                              style={{
                                justifyContent: 'center',
                                alignItems: 'center',
                                marginTop: 20,
                              }}>
                              <Button
                                type="default"
                                rounded
                                buttonStyle={{
                                  width: 150,
                                  marginBottom: 10,
                                }}
                                disabledStyle={{
                                  width: 150,
                                  marginBottom: 10,
                                }}
                                title={
                                  <SociollaText
                                    type="bold"
                                    style={{
                                      fontSize: 14,
                                      letterSpacing: 0.5,
                                      color: Colors.black,
                                    }}>
                                    Add Product
                                  </SociollaText>
                                }
                                onPress={() => onAddProduct()}
                              />
                            </View>
                          ) : (
                            false
                          )}
                        </View>
                      ) : (
                        false
                      )}
                      {loadingAddProduct ? (
                        <View
                          style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            justifyContent: 'center',
                            paddingVertical: 15,
                          }}>
                          <Layout.Loader />
                        </View>
                      ) : (
                        false
                      )}
                    </ScrollView>
                  ) : (
                    false
                  )}
                  {isWeb ? (
                    <View
                      style={{
                        width: '100%',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: Colors.grey4,
                        paddingHorizontal: 20,
                        paddingVertical: 12,
                      }}>
                      <SociollaText
                        type="bold"
                        style={{
                          fontSize: isWeb ? 16 : 13,
                          letterSpacing: 0.5,
                          color: Colors.grey1,
                        }}>
                        E-SIGNATURE
                      </SociollaText>
                    </View>
                  ) : (
                    false
                  )}
                  {page == 3 || isWeb ? (
                    <View
                      style={{
                        paddingHorizontal: 15,
                        paddingTop: 20,
                        flexDirection: isWeb ? 'row' : 'column',
                        justifyContent: 'space-between',
                        ...(isWeb
                          ? {
                              paddingHorizontal: 20,
                              marginBottom: 15,
                            }
                          : {
                              paddingBottom: !isMobileWeb
                                ? isAndroid
                                  ? 200
                                  : 150
                                : 180,
                            }),
                      }}>
                      <View
                        style={[
                          styles.rowForm,
                          { marginBottom: isWeb ? 0 : 50 },
                        ]}>
                        <Layout.FormGroup>
                          <ESignature
                            label="Requestor Signature"
                            esignImage={values.esign_requestor}
                            dateSigned={
                              values.esign_requestor
                                ? detailData?.esign_requestor?.created_at ||
                                  new Date()
                                : ''
                            }
                            onChange={(v) => {
                              setFieldValue('esign_requestor', v);
                            }}
                          />
                        </Layout.FormGroup>
                        <Layout.FormGroup>
                          <Input
                            label="Requestor Name"
                            value={values.esign_requestor_name}
                            isRequired
                            hasError={
                              errors.esign_requestor_name &&
                              touched.esign_requestor_name
                                ? errors.esign_requestor_name
                                : null
                            }
                            onChangeText={(value) => {
                              setFieldValue('esign_requestor_name', value);
                            }}
                          />
                        </Layout.FormGroup>
                      </View>
                      <View style={styles.rowForm}>
                        <Layout.FormGroup>
                          <ESignature
                            label="Approver Signature"
                            esignImage={values.esign_approver}
                            dateSigned={
                              values.esign_approver
                                ? detailData?.esign_approver?.created_at ||
                                  new Date()
                                : ''
                            }
                            onChange={(v) => {
                              setFieldValue('esign_approver', v);
                            }}
                          />
                        </Layout.FormGroup>
                        <Layout.FormGroup>
                          <Input
                            label="Approver Name"
                            value={values.esign_approver_name}
                            isRequired
                            hasError={
                              errors.esign_approver_name &&
                              touched.esign_approver_name
                                ? errors.esign_approver_name
                                : null
                            }
                            onChangeText={(value) => {
                              setFieldValue('esign_approver_name', value);
                            }}
                          />
                        </Layout.FormGroup>
                      </View>
                    </View>
                  ) : (
                    false
                  )}
                </ScrollView>
              </Layout.Wrapper>

              {!isWeb && (isEdit ? !getInventoryDetail?.loading : true) ? (
                <Layout.ButtonSticky
                  show={
                    props?.route?.name == 'InventoryFormScreen' &&
                    [1, 2, 3].includes(page)
                  }
                  route={props?.route}
                  buttons={
                    [1, 2].includes(page)
                      ? [
                          page == 2 && products.length > 0
                            ? {
                                title: 'Add Product',
                                type: 'default',
                                onPress: () => onAddProduct(),
                              }
                            : {},
                          {
                            title: 'Next',
                            disabled:
                              page == 1
                                ? !warehouseData
                                : page == 2
                                ? !products.length
                                : true,
                            onPress: () => setPage((prev) => prev + 1),
                          },
                        ]
                      : [
                          {
                            title: 'Submit',
                            disabled: !isAvailableSubmit(values),
                            onPress: (evt) => {
                              setFieldValue('status', 'submitted');
                              handleSubmit(evt);
                            },
                          },
                        ]
                  }
                />
              ) : (
                false
              )}
            </>
          )}
        </Formik>
      ) : (
        false
      )}

      <AddProduct
        show={showAddProduct}
        detail={productDetail}
        products={products}
        onClose={() => onCloseProduct()}
        setProducts={async ({ val, index }) => {
          setLoadingAddProduct(true);
          try {
            // Get Inventory stock
            await calculateAvailableStock(
              warehouseData.pacman_warehouse_id,
              formRef.current?.values?.inventory_type,
              formRef.current?.values?.salesman.id,
              val,
            );
          } catch (error) {
            Toast.show({
              text1: 'A system error occurred',
              type: 'error',
            });
          }
          setLoadingAddProduct(false);
        }}
      />
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  row: { flexDirection: 'row' },
  rowForm: { width: isWeb ? 'calc(50% - 20px)' : '100%' },
});

export default InventoryFormScreen;
